import React, { useEffect, useState, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Users, LifeBuoy, TrendingUp, Target, GraduationCap, Download, X } from "lucide-react";
import api, { API_ROOT } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { StatCard, Label, TrendPill, scoreColor, Avatar, IndustryCohort, InfoBadge } from "../../components/common";
import { TrendArea, Donut, RadialGauge } from "../../components/charts";
import FrontlineExperience from "../../components/FrontlineExperience";
import { Button } from "../../components/ui/button";
import RatingsCollected7d from "../../components/RatingsCollected7d";
import ResponsesSummary from "../../components/ResponsesSummary";
import TriggerLeaderboard from "../../components/TriggerLeaderboard";
import { feedbackHref } from "../../lib/feedbackLink";
import PeriodFilter, { periodLabel } from "../../components/PeriodFilter";
import ChannelSplit from "../../components/ChannelSplit";
import ExternalBenchmark from "../../components/ExternalBenchmark";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import StoreKpiTile from "../../components/StoreKpiTile";

export default function StoreDashboard() {
  const { user } = useAuth();
  const [params, setParams] = useSearchParams();
  const navigate = useNavigate();
  const [locations, setLocations] = useState([]);
  const [locId, setLocId] = useState(params.get("location") || "");
  const [d, setD] = useState(null);
  const [org, setOrg] = useState(null);
  const [teamFilter, setTeamFilter] = useState(null);
  const [days, setDays] = useState("7");

  const isCorp = user.role === "corporate_admin";

  const healthBucket = (ssi) => (ssi >= 4 ? "Healthy" : ssi >= 3.5 ? "Watch" : "At-risk");

  useEffect(() => {
    if (isCorp) api.get("/locations").then((r) => setLocations(r.data)).catch(() => {});
    api.get("/organization").then((r) => setOrg(r.data.organization)).catch(() => {});
  }, [isCorp]);

  const load = useCallback(() => {
    setTeamFilter(null);
    const q = `?days=${days}${locId ? `&location_id=${locId}` : ""}`;
    api.get(`/dashboard/store${q}`).then((r) => setD(r.data)).catch(() => {});
  }, [locId, days]);

  useEffect(() => { load(); }, [load]);

  const onSelect = (v) => { setLocId(v); setParams(v ? { location: v } : {}); };

  if (!d) return <div className="text-slate-400">Loading dashboard…</div>;
  const s = d.score;
  const rm = d.recovery_metrics;

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div className="flex items-center gap-3">
          {org?.logo_url && <img src={org.logo_url} alt={org.name} className="h-12 w-12 rounded-lg border border-slate-200 bg-white object-contain p-1" data-testid="store-dash-logo" />}
          <div>
            <Label>{org?.name || "Location Dashboard"}</Label>
            <h1 className="font-head text-2xl font-bold text-slate-900 sm:text-3xl">{d.location.name}</h1>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <PeriodFilter value={days} onChange={setDays} testId="store-period" />
          {isCorp && (
            <Select value={locId} onValueChange={onSelect}>
              <SelectTrigger className="w-48" data-testid="location-select"><SelectValue placeholder="Choose location" /></SelectTrigger>
              <SelectContent>
                {locations.map((l) => <SelectItem key={l.id} value={l.id} data-testid={`loc-opt-${l.id}`}>{l.name}</SelectItem>)}
              </SelectContent>
            </Select>
          )}
          <a href={`${API_ROOT}/reports/employees.csv`} data-testid="export-employees-csv">
            <Button variant="outline"><Download className="mr-2 h-4 w-4" /> Export</Button>
          </a>
        </div>
      </div>

      <FrontlineExperience locationId={locId || d.location?.id} />

      {(() => {
        const emps = d.employees_detail || [];
        const healthy = emps.filter((e) => e.score.ssi >= 4).length;
        const watch = emps.filter((e) => e.score.ssi >= 3.5 && e.score.ssi < 4).length;
        const atRisk = emps.filter((e) => e.score.ssi < 3.5).length;
        const resolved = Math.max(0, rm.total_cases - rm.open_cases);
        return (
          <div className="grid gap-6 sm:grid-cols-3">
            <div className="rounded-xl border border-slate-200 bg-white p-5" data-testid="store-gauge-index">
              <div className="flex items-center justify-between">
                <span className="flex items-center gap-1.5"><Label>Service Index</Label>
                  <InfoBadge title="Service Index" testId="store-index-info"
                    text="This location's overall service score out of 100, based on verified customer ratings. It's the headline number for how well this team is serving customers." /></span>
                <TrendPill value={d.trend_pct} testId="store-trend" />
              </div>
              <RadialGauge value={s.ssi_percent} color={scoreColor(s.ssi)} label="/ 100" testId="store-radial-index" />
              <p className="text-center text-xs text-slate-400">{Math.round(s.confidence * 100)}% confidence · {s.verified_interactions} verified interactions</p>
              {!s.meets_threshold && <p className="mt-1 text-center text-xs text-amber-600">Below {s.min_reviews}-review threshold — interpret with caution.</p>}
            </div>
            <div className="rounded-xl border border-slate-200 bg-white p-5" data-testid="store-donut-team">
              <span className="flex items-center gap-1.5"><Label>Team Health</Label>
                <InfoBadge title="Team Health" testId="team-health-info"
                  text="A breakdown of this location's employees by how they're scoring: Healthy (strong), Watch (okay but slipping) and At-risk (needs coaching). It shows how much of your team needs support." /></span>
              <Donut testId="team-health-donut" centerValue={emps.length} centerLabel="employees"
                onSlice={setTeamFilter} activeName={teamFilter}
                data={[
                  { name: "Healthy", value: healthy, color: "#10B981" },
                  { name: "Watch", value: watch, color: "#F59E0B" },
                  { name: "At-risk", value: atRisk, color: "#EF4444" },
                ]} />
              <p className="mt-1 text-center text-xs text-slate-400">Click a slice to filter the team below</p>
            </div>
            <div className="rounded-xl border border-slate-200 bg-white p-5" data-testid="store-donut-recovery">
              <span className="flex items-center gap-1.5"><Label>Service Recovery</Label>
                <InfoBadge title="Service Recovery" testId="store-recovery-info"
                  text="Service recovery is following up unhappy customers to make things right. This shows how many cases are still open versus resolved, and how fast your team responds." /></span>
              <Donut testId="store-recovery-donut" centerValue={rm.total_cases} centerLabel={`cases · ${periodLabel(d.days).toLowerCase()}`} onSlice={() => navigate("/app/recovery")}
                data={[
                  { name: "Open", value: rm.open_cases, color: "#EF4444" },
                  { name: "Resolved", value: resolved, color: "#10B981" },
                ]} />
              <p className="text-center text-xs text-slate-400">
                {rm.avg_response_hours != null ? `~${rm.avg_response_hours}h to respond` : "—"} · {rm.avg_resolution_hours != null ? `~${rm.avg_resolution_hours}h to resolve` : "—"}
              </p>
            </div>
          </div>
        );
      })()}

      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        <StatCard label="Verified Interactions" value={s.verified_interactions} sub={periodLabel(d.days)} testId="store-interactions" onClick={() => navigate(feedbackHref({ location_id: d.location.id, days: d.days }))}
          help={{ title: "Verified Interactions", text: "The number of customer ratings for this location that passed our anti-gaming checks, so fake or suspicious ratings don't skew the picture." }}><Users className="h-6 w-6 text-slate-300" /></StatCard>
        <StatCard label="Top Service Strength" value={d.top_strength?.value != null ? `${d.top_strength.value}%` : "—"} sub={d.top_strength?.label || "—"} accent="#10B981" testId="store-strength"
          help={{ title: "Top Service Strength", text: "The part of the customer experience this team does best (e.g. making customers feel welcomed). Lean on this as a strength to be proud of." }}><TrendingUp className="h-6 w-6 text-slate-300" /></StatCard>
        <StatCard label="Biggest Gap" value={d.gap?.gap_score != null ? `${d.gap.gap_score}%` : "—"} sub={d.gap?.gap_label || "—"} accent="#EF4444" testId="store-gap"
          help={{ title: "Biggest Gap", text: "The weakest part of the customer experience for this team — the area where improvement will lift your score the most." }}><Target className="h-6 w-6 text-slate-300" /></StatCard>
        <StatCard label="Needs Coaching" value={d.coaching_count} sub="Weak-touchpoint employees" accent="#F59E0B" testId="store-coaching"
          help={{ title: "Needs Coaching", text: "How many employees have a notably weak area in their customer interactions and would benefit from some targeted coaching." }}><GraduationCap className="h-6 w-6 text-slate-300" /></StatCard>
      </div>

      <ChannelSplit channels={d.channels} days={d.days} locationId={d.location.id} testId="store-channel-split" />

      <IndustryCohort you={s.ssi} industry={d.industry_index} label={d.industry} />

      <ExternalBenchmark locationId={d.location.id} ssi={s.ssi_percent} canEdit={user.role !== "employee"} />

      <div className="grid gap-6 md:grid-cols-3">
        <div className="rounded-xl border border-slate-200 bg-white p-5 md:col-span-2">
          <span className="flex items-center gap-1.5"><Label>Service Trend · {d.trend_days} days</Label>
            <InfoBadge title="Service Trend" testId="store-trend-info"
              text="This location's daily Service Index for the selected period (at least the last 7 days). Use it to see whether service here is improving, flat, or slipping." /></span>
          <div className="mt-3"><TrendArea data={d.trend} testId="store-trend-chart" /></div>
        </div>
        <div className="space-y-4">
          {d.gap?.recommended_training ? (
            <div className="rounded-xl border border-indigo-200 bg-indigo-50 p-4" data-testid="store-reco">
              <Label className="text-indigo-700">Recommended Training</Label>
              <p className="mt-1 text-sm font-semibold text-indigo-900">{d.gap.recommended_training}</p>
              <p className="text-xs text-indigo-600">Est. {d.gap.estimated_minutes} minutes · addresses {d.gap.gap_label}</p>
            </div>
          ) : (
            <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-4" data-testid="store-reco-clear">
              <Label className="text-emerald-700">No critical gaps</Label>
              <p className="mt-1 text-sm font-semibold text-emerald-900">This team is tracking well across every touchpoint.</p>
            </div>
          )}
        </div>
      </div>

      <RatingsCollected7d testId="store-ratings-7d" initialLocation={d.location?.id} />

      <StoreKpiTile locationId={d.location?.id} testId="store-kpi-tile" />

      <ResponsesSummary locationId={d.location?.id} days={d.days} testId="store-responses-summary" />
      <TriggerLeaderboard days={d.days} testId="store-trigger-leaderboard" />

      <div className="rounded-xl border border-slate-200 bg-white p-5">
        <div className="flex items-center justify-between">
          <span className="flex items-center gap-1.5"><Label>Employee Performance</Label>
            <InfoBadge title="Employee Performance" testId="emp-perf-info"
              text="Each employee's individual Service Index, based on customer feedback that mentioned them. A low badge flags their weakest area. Click anyone to see their full profile." /></span>
          <div className="flex items-center gap-2">
            {teamFilter && (
              <button onClick={() => setTeamFilter(null)} data-testid="clear-team-filter"
                className="inline-flex items-center gap-1.5 rounded-full bg-indigo-50 px-3 py-1 text-xs font-bold text-indigo-600 hover:bg-indigo-100">
                {teamFilter} only <X className="h-3.5 w-3.5" />
              </button>
            )}
            <span className="text-xs text-slate-400">
              {(() => { const n = teamFilter ? d.employees_detail.filter((e) => healthBucket(e.score.ssi) === teamFilter).length : d.employees_detail.length; return `${n} employee${n === 1 ? "" : "s"}`; })()}
            </span>
          </div>
        </div>
        <div className="mt-3 grid gap-3 sm:grid-cols-2 lg:grid-cols-3" data-testid="emp-grid">
          {d.employees_detail.filter((e) => !teamFilter || healthBucket(e.score.ssi) === teamFilter).map((e) => (
            <button key={e.id} data-testid={`emp-card-${e.id}`} onClick={() => navigate(`/app/employees/${e.id}`)}
              className="flex items-center gap-3 rounded-xl border border-slate-200 p-3 text-left transition-transform hover:-translate-y-0.5 hover:border-indigo-300">
              <Avatar name={e.name} src={e.photo_url} size={44} />
              <div className="min-w-0 flex-1">
                <p className="truncate font-semibold text-slate-800">{e.name}</p>
                <p className="truncate text-xs text-slate-400">{e.job_title || "—"}</p>
                {e.gap?.gap_score != null && e.gap.gap_score < 70 && (
                  <span className="mt-1 inline-block rounded-full bg-amber-50 px-2 py-0.5 text-[10px] font-bold uppercase text-amber-600">Gap: {e.gap.gap_label}</span>
                )}
              </div>
              <span className="sv-metric text-lg font-bold" style={{ color: scoreColor(e.score.ssi) }}>{e.score.ssi.toFixed(1)}</span>
            </button>
          ))}
          {teamFilter && d.employees_detail.filter((e) => healthBucket(e.score.ssi) === teamFilter).length === 0 && (
            <p className="col-span-full py-6 text-center text-sm text-slate-400">No employees in the "{teamFilter}" band.</p>
          )}
        </div>
      </div>
    </div>
  );
}
